import React, { useState, useEffect } from "react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import { Calendar, Trash2, CheckCircle, XCircle, Pencil } from "lucide-react";
import * as LucideIcons from "lucide-react";
import LocalDateTime from "services/date-converter";
import Pagination from "modules/swiftboard/components/Pagination";
import sortData from "utils/sorting-coloum";
import { useAlert } from "context/AlertContext";
import apiModuleMaster from "./api";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { ModuleBadge } from "layouts/SidebarBadge";
import { moduleMasterFields } from "./features/module-master.fields";
import { moduleMasterValidation } from "./features/module-master.validation";
import { loadModuleMasterDependencies } from "./features/module-master.dependencies";
import { getRoleConfig } from "../role-config";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import { moduleMasterFilterFields, loadModuleMasterFilterDependencies } from "./features/module-master.filter";

const EMPTY_FILTERS = {};

const ModuleMaster = ({ onTotalCountChange }) => {
  const { showAlert } = useAlert();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const [confirmData, setConfirmData] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Yes",
    cancelText: "No",
    icon: null,
    resolve: null,
  });

  const fetchCountries = async () => {
    setLoading(true);
    try {
      const payload = {
        searchText: searchText,
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        search: JSON.stringify(filters),
      };


      const response = await apiModuleMaster.getAllModuleMaster(payload);

      if (response.data.status) {
        // showAlert({ type: 'success', message: response.data?.message });

        setData(response.data.data);
        setTotalCount(response.data.total_count || 0);
        onTotalCountChange?.(response.data.total_count || 0);
      }
      else {
        showAlert({ type: 'warning', message: response.data?.message });
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      if (error.response) {
        showAlert({ type: 'error', message: error.response?.data?.message });
      } else {
        showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
      }
    }
    finally {
      setLoading(false);
    }
  }

  const handleToggleStatus = async (row) => {
    try {
      const isCurrentlyActive = row.is_activated;

      const confirm = await openConfirm({
        title: isCurrentlyActive ? "Deactivate Module" : "Activate Module",
        message: isCurrentlyActive
          ? "After deactivating this module, all assigned users will not be able to access this module. Are you sure you want to deactivate this module?"
          : "Are you sure you want to activate this module?",
        confirmText: isCurrentlyActive ? "Deactivate" : "Activate",
        cancelText: "Cancel",
        icon: isCurrentlyActive
          ? <XCircle size={24} className="text-danger" />
          : <CheckCircle size={24} className="text-success" />,
      });

      if (!confirm) return;

      const response = await apiModuleMaster.toggleStatus(row.id);

      if (response.data.status) {
        showAlert({
          type: "success",
          message: response.data?.message
        });
        fetchCountries();
      } else {
        showAlert({ type: "warning", message: response.data?.message });
      }
    } catch (error) {
      showAlert({
        type: "error",
        message: error.response?.data?.message || "Something went wrong"
      });
    }
  };

  const handleDeleteSelected = async (ids = []) => {

    if (ids.length === 0) {
      showAlert({ type: 'warning', message: "Please select at least one country to delete." });
      return;
    }

    const confirmed = await openConfirm({
      title: "Delete Module",
      message:
        ids.length > 1
          ? `Are you sure you want to delete ${ids.length} selected Modules? This action cannot be undone.`
          : "Are you sure you want to delete this Module? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <Trash2 size={24} className="text-danger" />,
    });

    if (!confirmed) return;
    try {
      const response = await apiModuleMaster.delete(ids)

      if (response.data.status) {
        showAlert({ type: 'success', message: response.data?.message });

        fetchCountries();
        setSelectedRows([]);
      }
      else {
        showAlert({ type: 'warning', message: response.data?.message });
      }
    }
    catch (error) {
      if (error.response) {
        showAlert({ type: 'error', message: error.response?.data?.message });
      } else {
        showAlert({ type: 'error', message: "An unexpected error occurred. Please try again!" });
      }
    }
  };

  useEffect(() => {
    fetchCountries();
  }, [currentPage, pageSize, searchText, filters, resetTrigger]);

  useEffect(() => {
    const sorted = sortData(data, sortConfig);
    setFilteredData(sorted);
  }, [data, sortConfig]);

  const openConfirm = (options) => {
    return new Promise((resolve) => {
      setConfirmData({
        ...options,
        isOpen: true,
        resolve,
      });
    });
  };

  const handleConfirmClose = (result) => {
    if (confirmData.resolve) {
      confirmData.resolve(result);
    }

    setConfirmData((prev) => ({
      ...prev,
      isOpen: false,
      resolve: null,
    }));
  };
  const handleSearch = (value) => {
    setSearchText(value)
    setCurrentPage(1); // reset to first page
  };

  const hasActiveQuery = !!searchText || Object.keys(filters).length > 0;

  const handleApplyFilters = ({ search }) => {
    setFilters(search);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page, newPageSize = pageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };



  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleCreate = () => {
    setSelectedModuleId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (id) => {
    setSelectedModuleId(id);
    setIsModalOpen(true);
  };


  const handleRefresh = () => {
    setSearchText('');// to reset search text
    setFilters(EMPTY_FILTERS);// to reset advanced filters
    setCurrentPage(1);// to reset page number to 1
    // Bumping resetTrigger clears the search bar and filter drawer, and refetches through
    // the effect once the cleared state is in place (a direct fetch here would send the old one).
    setResetTrigger(prev => prev + 1);
  };

  const columns = [
    {
      key: "icon",
      label: "Icon",
      width: "20px",
      render: (row) => {
        const IconComponent = LucideIcons[row.icon]; // icon name from DB

        return IconComponent ? (
          <div className="flex items-center gap-2">
            <IconComponent className="w-5 h-5 text-primary" />
            {/* <span className="text-sm text-text_secondary">{row.icon}</span> */}
          </div>
        ) : (
          <span className="text-text_muted">NA</span>
        );
      },
    },
    {
      key: "name",
      label: "Module Name",
      width: "220px",
      sortable: true,
    },
    {
      key: "role_mappings",
      label: "Assigned Roles",
      width: "260px",
      render: (row) => {
        const roles = (row.role_mappings || []).map((m) => m.user_role).filter(Boolean);
        if (!roles.length) {
          return <span className="text-xs text-text_muted">Not assigned</span>;
        }

        return (
          <div className="flex flex-wrap items-center gap-1">
            {roles.map((r) => {
              const config = getRoleConfig(r.role);
              const Icon = config.icon;
              return (
                <span
                  key={r.id}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${config.className}`}
                >
                  <Icon size={12} />
                  {config.label}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      key: "parent_module_name",
      label: "Parent Module",
      width: "140px",
      render: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${row.parent_module?.name
            ? "bg-success_bg text-success border border-success_border"
            : "bg-danger_bg text-danger border border-danger_border"
            }`}
        >
          {row?.parent_fid ? row.parent_module?.name : "N/A"}
        </span>
      ),
    },
    {
      key: "path",
      label: "Path",
      width: "220px",
      sortable: true,
      render: (row) => (
        <span
          className="inline-block max-w-[200px] truncate px-3 py-1 rounded-full text-xs font-medium bg-info_bg text-info border border-info_border"
          title={row.path}
        >
          {row.path || "N/A"}
        </span>
      ),
    },
    {
      key: "is_activated",
      label: "Status",
      width: "140px",
      render: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${row.is_activated
            ? "bg-success_bg text-success border border-success_border"
            : "bg-danger_bg text-danger border border-danger_border"
            }`}
        >
          {row.is_activated ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "badge",
      label: "Badge",
      width: "130px",
      sortable: true,
      render: (row) =>
        row.badge ? (
          <ModuleBadge badge={row.badge} />
        ) : (
          <span className="text-xs text-text_muted">None</span>
        ),
    },
    {
      key: "created_date",
      label: "Created Date",
      width: "140px",
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.created_date} type="datetime" />
        </div>
      ),
      sortable: true,
    },
    {
      key: "updated_date",
      label: "Last Update",
      width: "140px",
      render: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-text_primary">
          <Calendar size={12} className="text-text_muted" />
          <LocalDateTime utcTime={row.updated_date} type="datetime" />
        </div>
      ),
      sortable: true,
    },
  ];

  // Custom action buttons for each row
const renderRowActions = (row) => (
  <div className="flex justify-end">
    <div className="flex items-center gap-1 px-2 py-1
                    bg-surface border border-border
                    rounded-lg ">

      {/* Edit */}
      <button
        className="p-1 rounded-md text-info"
        onClick={(e) => {
          e.stopPropagation();
          handleEdit(row.id);
        }}
        title="Edit"
      >
        <Pencil size={15} strokeWidth={2.2} />
      </button>

      {/* Activate / Deactivate */}
      <button
        className={`p-1 rounded-md transition-colors
        ${row.is_activated
            ? "text-danger hover:bg-danger_bg"
            : "text-success hover:bg-success_bg"
          }`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggleStatus(row);
        }}
        title={row.is_activated ? "Deactivate" : "Activate"}
      >
        {row.is_activated ? (
          <XCircle size={16} strokeWidth={2.2} />
        ) : (
          <CheckCircle size={16} strokeWidth={2.2} />
        )}
      </button>

      {/* Delete */}
      <button
        className="p-1 rounded-md text-danger hover:bg-danger_bg transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteSelected([row.id]);
        }}
        title="Delete"
      >
        <Trash2 size={15} />
      </button>

    </div>
  </div>
);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface_alt">
      <div className="flex flex-col h-full gap-4">
        <TableToolBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onCreate={handleCreate}
          onDeleteSelected={() => handleDeleteSelected(selectedRows)}
          selectedCount={selectedRows.length}
          resetSearchTrigger={resetTrigger}
          advanceFilter={
            <GlobalAdvanceFilter
              fields={moduleMasterFilterFields}
              dependenciesLoader={loadModuleMasterFilterDependencies}
              onApply={handleApplyFilters}
              resetKey={resetTrigger}
            />
          }
        />


        {data?.length === 0 && !loading ? (<div className="flex-1 flex items-center justify-center border rounded-lg border-border bg-surface">
          <NoDataState
            icon="package"
            title="No Module Found"
            message={hasActiveQuery ? "No modules match your search or filters." : "There is no data available for this section."}
            showRefresh={false}
            showBack={false}
            size="medium"
          />
        </div>) : (<>
          <div className="flex-1 overflow-hidden border rounded-lg border-border">
            <TableGrid
              data={filteredData}
              totalCount={totalCount}
              loading={loading}
              currentPage={currentPage}
              columns={columns}
              sortConfig={sortConfig}
              onSort={handleSort}
              onSelectionChange={setSelectedRows}
              pageSize={pageSize}
              renderActions={renderRowActions}
            />
          </div>

          <Pagination
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />


          {confirmData.isOpen && (
            <ConfirmDialogue
              confirmData={confirmData}
              handleConfirmClose={handleConfirmClose}
            />
          )}
        </>)}

        <EntityFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          id={selectedModuleId}
          title="Module"
          fetchById={apiModuleMaster.getById}
          createFn={apiModuleMaster.create}
          updateFn={apiModuleMaster.update}
          dependenciesLoader={loadModuleMasterDependencies}
          fields={moduleMasterFields}
          validationSchema={moduleMasterValidation}
          onSuccess={fetchCountries}
        />










      </div>
    </div>
  );
};

export default ModuleMaster;